import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Form } from "@/components/ui/form/form";
import { TextInput } from "@/components/ui/form/text-input";
import { useModalError } from "@/hooks/use-modal-error";
import { archiveContextPackSchema, ContextPack } from "@/api/studio/contexts/types/context.type";
import { useArchiveContextPack } from "@/api/studio/contexts/queries";
import { useSegment } from "@/hooks/use-segment";

/* Prop Types */
interface ArchiveContextModalProps {
  contextPack: ContextPack;
}

/*
 * ArchiveContextPackModal component
 * This component is used to archive a context pack
 */
export const ArchiveContextPackModal = ({ contextPack }: ArchiveContextModalProps) => {
  /* Stores */
  const [, navigate] = useLocation();

  /* Queries */
  const { mutateAsync: archiveContextPack, isPending: isArchiveContextPackPending, isError: isArchiveContextPackError } = useArchiveContextPack();

  /* Hooks */
  const { close } = useModalError({ error: isArchiveContextPackError });
  const { segmentTrackEvent } = useSegment();

  /* Handlers */
  const handleSave = async (values: { isArchived: boolean }) => {
    await archiveContextPack({ contextPackId: contextPack.id, values });

    // Segment event
    segmentTrackEvent("Context Pack Archived", {
      contextPackId: contextPack.id,
      name: contextPack.name,
      contextCount: contextPack?.contextCount,
    });

    // Close modal and navigate to contexts page
    navigate("/contexts");
    close();
  };

  /* Render */
  return (
    <Form onSubmit={handleSave} schema={archiveContextPackSchema} options={{ defaultValues: { isArchived: true } }}>
      {({ register }) => (
        <>
          {/* Header */}
          <div className="flex items-center justify-between pb-4">
            <h2>Archive context pack</h2>
            <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
          </div>

          {/* Details */}
          <div className="pb-4">
            <p>
              This context pack will be archived. All studies using this context won’t be affected. You can find it later in the ‘Archived’ tab on the context page. You can restore
              it anytime.
            </p>
          </div>
          <TextInput type="hidden" registration={register("isArchived")} className="mb-3" />

          {/* Buttons */}
          <div className="flex justify-end pt-4">
            <Button type="submit" variant="primary" disabled={isArchiveContextPackPending}>
              {isArchiveContextPackPending ? "Archiving..." : "Archive context pack"}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
